import React, { useState, useMemo } from 'react';
import { useScrollRestore } from '../../Hooks/useScrollRestore';
import FeedbackDialog from './FeedbackDialog';
import { useParams } from 'react-router';
import PackReviewHeader from './PackReviewHeader';
import CardListModal from './CardListModal';
import { useGetPackDetailsQuery, useGetPackItemReviewsQuery, useSubmitPackReviewMutation, useLogUserActionMutation } from '../../../api/packSlice';
import { useNavigate } from 'react-router';
import { Stack, Grid, Typography } from '@mui/material';
import LoadingIndicator from '../../LoadingIndicator';
import PackItemEntry from './PackItemEntry';
import { AppBar } from '@mui/material';
import PackCompleteModal from './PackCompleteModal';
import { useLocation } from 'react-router-dom';
import SubmitReviewButton from '../SubmitReviewButton';
import PackItemDetails from '../ItemDetails/PackItemDetails';

function PackReview() {
    const { save: saveScroll, restore: restoreScroll } = useScrollRestore();
    const location = useLocation();
    const campaignId = location.state?.campaignId ? Number(location.state.campaignId) : null;
    const packReviewId = Number(useParams().packReviewId);
    const physicianId = Number(useParams().physicianId);
    const { data: pack, isLoading, isSuccess } = useGetPackDetailsQuery({ cardOwnerId: physicianId, packReviewId: packReviewId });
    const { data: packItems, isSuccess: isSuccessItems } = useGetPackItemReviewsQuery({ cardOwnerId: physicianId, packReviewId: packReviewId });
    const [submitPackReview, { isLoading: isSubmittingChanges }] = useSubmitPackReviewMutation();
    const [submitUserAction] = useLogUserActionMutation();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isShowingCompleteModal, setIsShowingCompleteModal] = useState(false);
    const [isFeedbackDialogOpen, setisFeedbackDialogOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isItemDetailsOpen, setIsItemDetailsOpen] = useState(false);

    const navigateToPacks = async () => {
        // Log exit from pack
        try {
            submitUserAction({ action: 'exit_from_pack_to_pack_list', data: { packReviewId, physicianId, campaignId } }).unwrap();
        } catch (err) {
            console.error('Failed to log exit from pack:', err);
        }
        navigate(`/physician/${physicianId}/packs`, { state: { 
            campaignId: campaignId
        }});
    };

    const toggleModal = (value) => {
        setIsModalOpen(value);
        submitUserAction({
            action: 'view_pack_cards',
            data: { packReviewId, physicianId, campaignId }
        }).unwrap().catch(err => console.error('Failed to log user action:', err));
    };

    // Helper to count reviewed items
    const reviewedCount = useMemo(() => {
        if (!packItems) return 0;
        return packItems.filter(item => item.status === 'reviewed').length;
    }, [packItems]);

    const handleSubmitClick = () => {
        const totalItems = packItems ? packItems.length : 0;
        const threshold = Math.ceil(totalItems * 0.1); // 10% rounded up
        if (reviewedCount < threshold) {
            setisFeedbackDialogOpen(true);
        } else {
            submitReview();
        }
    };

    const submitReview = async (newFeedback) => {
        try {
            await submitPackReview({ cardOwnerId: physicianId, packReviewId: packReviewId, status: 'submitted', feedback: newFeedback }).unwrap();
            submitUserAction({ action: 'submit_pack_review', data: { packReviewId, physicianId, campaignId } }).unwrap().catch(err => console.error('Failed to log user action:', err));
            setIsShowingCompleteModal(true);
        } catch (err) {
            console.error('Failed to submit pack review:', err);
        }
    };

    // Group and sort items by category, memoized (data only)
    const groupedCategories = useMemo(() => {
        if (!isSuccessItems || !packItems) return [];
        const grouped = packItems.reduce((acc, item) => {
            const cat = item.category || 'Uncategorized';
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(item);
            return acc;
        }, {});
        return Object.keys(grouped)
            .sort((a, b) => a.localeCompare(b))
            .map(category => ({
                category,
                items: grouped[category]
            }));
    }, [isSuccessItems, packItems]);

    return (
        <Grid container sx={{ minHeight: '100vh', backgroundColor: '#F5F5F5' }}>
            <Grid item md={3} />
            <Grid item xs={12} md={6}>
                {isLoading && <LoadingIndicator />}
                {isSuccess && 
                <Stack>
                    <PackReviewHeader title={pack.pack_name} backText="All Packs" onClickBack={navigateToPacks} showCards={true} showModal={() => toggleModal(true)} numCards={pack.unique_cards_count} />
                    <CardListModal open={isModalOpen} onClose={() => toggleModal(false)} pack={pack} />
                    <PackCompleteModal open={isShowingCompleteModal} setOpen={setIsShowingCompleteModal} physicianId={physicianId} handleReturnToPacks={navigateToPacks} />
                    <PackItemDetails packItem={selectedItem} open={isItemDetailsOpen} onClose={() => { setIsItemDetailsOpen(false); restoreScroll(); }} />
                    <FeedbackDialog
                        open={isFeedbackDialogOpen}
                        packFeedback={pack.feedback}
                        onSubmit={(newFeedback) => {
                            setisFeedbackDialogOpen(false);
                            submitReview(newFeedback);
                        }}
                        onClose={() => {
                            setisFeedbackDialogOpen(false);
                        }}
                    />
                    <Stack spacing={2} padding={2}>
                        {groupedCategories.map(({ category, items }) => (
                            <Stack key={category} spacing={2} py={1}>
                                <Typography variant="heavy" fontSize='14px'>{category}</Typography>
                                {items.map(item => (
                                    <PackItemEntry key={item.id} itemReview={item} cardOwnerId={physicianId} packReviewId={packReviewId} onClick={() => { saveScroll(); setSelectedItem(item); setIsItemDetailsOpen(true); }} />
                                ))}
                            </Stack>
                        ))}
                    </Stack>
                    <AppBar position='sticky' elevation={10} sx={{ marginTop: 5, paddingLeft: 3, paddingRight: 3, bottom: 16, backgroundColor: '#FFFFFF0F', alignSelf: 'center', boxShadow: 0 }}>
                        <SubmitReviewButton onClick={handleSubmitClick} loading={isSubmittingChanges} variant="feedback" />
                    </AppBar>
                </Stack>
                }
            </Grid>
        </Grid>
    );
}

export default PackReview;
